/*
 * src/pages/reports/_money.js — ONE money formatter for the analytics family.
 *
 * ⛔⛔ WHY THIS FILE EXISTS. Live, 28 Oct 2026: the Open-pipeline-value tile read
 * $3,265,035 while its own grouped summary showed a single group at
 * "$97,090,900" — roughly 100× the whole tile. Nothing was double-counted. The
 * summary was handed INTEGER CENTS and rendered them with a formatter that
 * assumes DOLLARS.
 *
 * The cause was not one careless call site. At the time of the bug there were
 * SIX independent `money()` definitions under src/pages/reports/ — CeoDashboard,
 * MetricDrilldown, FunnelReport, LeadSourceReport, FinancePanel and
 * MetricSummary — every one of them a bare
 * `$${Number(v).toLocaleString(...)}`, and every one of them silently assuming
 * its argument was dollars. A function that cannot say what unit it takes cannot
 * be called wrongly, because there is nothing to get wrong until the number is
 * on screen.
 *
 * ⭐ SO THE FIX IS THE NAME, NOT THE MATH. `formatCents` and `formatDollars`
 * make the unit part of the call. `formatCents(row.value)` reads wrong where
 * `money(row.value)` read fine, and that is the whole point: the wrongness moves
 * from the rendered output, where only a human comparing two screens can catch
 * it, to the source, where a reader and a test can.
 */

/** Integer cents → "$1,234.56". The ONLY correct formatter for `*_cents` fields. */
export function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return `$${(n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Integer cents → "$1,234" (no decimals), for headline figures. */
export function formatCentsRounded(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return `$${Math.round(n / 100).toLocaleString('en-US')}`;
}

/** A dollar amount → "$1,234.56". For NUMERIC columns that are already dollars. */
export function formatDollars(dollars) {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/*
 * ⚠ A non-finite input renders "—", NOT "$0.00". A missing or unparseable
 * amount is not zero, and "$0.00" is indistinguishable from a measured zero —
 * the same false-zero shape this programme keeps finding, one layer down in the
 * formatter.
 */

/** Integer cents → a Number of dollars, for arithmetic rather than display. */
export function centsToDollars(cents) {
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : NaN;
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * TYPED MONEY — Card P258 (Daniel, 3 Nov 2026): "the target input must format
 * the number as it's typed so it can't be entered wrong".
 *
 * THE CONVERSION IS STRING ARITHMETIC: split on the decimal point, pad the
 * fraction to exactly two places, concatenate. No float is constructed.
 *
 * ⚠⚠ AND AN HONEST ACCOUNT OF WHAT THAT BUYS, BECAUSE I FIRST WROTE A
 * STRONGER CLAIM HERE AND THE MEASUREMENT REFUTED IT.
 *
 * The claim was that `Math.round(Number(s) * 100)` is wrong for ordinary
 * amounts — "260000.10 * 100 === 26000010.000000004", "1.005 * 100 ===
 * 100.49999999999999". Both products are real. But `Math.round` repairs both,
 * and sanitizeMoneyInput has ALREADY clamped the input to at most two
 * decimals before either function sees it. MEASURED over 285,715 two-decimal
 * values from $0.00 to $20,000.00: the two implementations differ on NONE.
 * A planted float version passed all 24 arms of the input suite — the clamp
 * was the second mechanism doing the work.
 *
 * ⛔⛔ AND THE SECOND CLAIM WAS WRONG TOO, SO IT IS WRITTEN DOWN RATHER THAN
 * QUIETLY CORRECTED. I then argued the string form "fails closed past
 * MAX_SAFE_INTEGER where the float form lies". Planting the float version
 * AGAIN left all 25 arms green: the `Number.isSafeInteger(cents)` check below
 * is what refuses the huge value, and it sits downstream of both forms.
 *
 * ⚠⚠ THE HONEST POSITION, STATED RATHER THAN IMPLIED — the idiom this repo
 * already uses for tzMidnight's second pass in routes/_date_presets.js:
 *
 *   TWO MECHANISMS DO THE WORK, AND NEITHER IS THE STRING ARITHMETIC.
 *     · sanitizeMoneyInput clamps to two decimals, which removes the float
 *       residue before it can matter; and
 *     · Number.isSafeInteger refuses magnitudes where any form loses
 *       precision.
 *   NO RED ARM ISOLATES THE STRING CONVERSION, because within the domain
 *   those two leave, it is INDISTINGUISHABLE from Math.round(Number(s)*100).
 *   Measured: 285,715 two-decimal values, zero disagreements; a planted float
 *   implementation passed every arm, twice.
 *
 * ⭐ It is kept because it is exact without depending on either — the clamp
 * lives in a different function written for live formatting, and a future
 * edit relaxing it would make a float conversion lossy with nothing to say
 * so. That is a defence-in-depth argument, not a correctness one, and a
 * reader should not go looking for the test that proves otherwise.
 */

/** The digits a money field accepts: optional thousands separators, ≤2 decimals. */
const MONEY_SHAPE = /^\d+(\.\d{0,2})?$/;

/**
 * Strip everything a person might type or paste around an amount, keeping the
 * first decimal point only.
 *
 * ⚠ A SANITISER, NOT A VALIDATOR — it is what makes live formatting possible
 * while typing, and it is deliberately separate from `parseDollarsToCents`,
 * which REFUSES what it does not understand. Collapsing the two would make the
 * field silently accept "2x6" as 26.
 */
export function sanitizeMoneyInput(raw) {
  const s = String(raw == null ? '' : raw);
  let out = '';
  let seenDot = false;
  for (const ch of s) {
    if (ch >= '0' && ch <= '9') { out += ch; continue; }
    // ⚠ The FIRST decimal point only; a second is ignored, not an error — the
    // user is mid-keystroke and an error message on every stray key is noise.
    if ((ch === '.' || ch === ',') && !seenDot && ch === '.') { out += '.'; seenDot = true; }
    // Commas, "$", spaces and letters are dropped. Pasting "$260,000.00" works.
  }
  // ⚠ Clamp to two decimals so the field cannot hold a value it cannot store.
  const dot = out.indexOf('.');
  if (dot !== -1) out = out.slice(0, dot + 3);
  return out;
}

/**
 * Sanitised digits → the string shown in the box, with live thousands
 * separators. The `$` is rendered OUTSIDE the input (fixed prefix), so it can
 * never be deleted, duplicated or land in the middle of the value.
 *
 * ⚠ A TRAILING "." AND TRAILING ZEROS ARE PRESERVED. "260000." and "260000.0"
 * are states a person passes through while typing; normalising them away moves
 * the caret and deletes the character just entered.
 */
export function formatMoneyInput(sanitized) {
  const s = String(sanitized == null ? '' : sanitized);
  if (s === '') return '';
  const [intPart, fracPart] = s.split('.');
  const grouped = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return s.includes('.') ? `${grouped}.${fracPart}` : grouped;
}

/**
 * A typed amount → INTEGER CENTS, or null when it is not a valid amount.
 *
 * ⛔ NULL, never 0, for unparseable input. Zero is an amount somebody chose.
 * ⛔ NO FLOAT IS EVER CONSTRUCTED — see the header.
 */
export function parseDollarsToCents(raw) {
  const s = sanitizeMoneyInput(raw);
  if (s === '' || !MONEY_SHAPE.test(s)) return null;
  const [intPart, fracRaw = ''] = s.split('.');
  const frac = (fracRaw + '00').slice(0, 2);     // pad, never round
  const digits = `${intPart}${frac}`.replace(/^0+(?=\d)/, '');
  const cents = Number(digits);
  // ⚠ Refuse rather than silently lose precision beyond Number's safe range.
  return Number.isSafeInteger(cents) ? cents : null;
}
